"use client";

import { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import type {
  CEventsCalendarArticle,
  CEventsCalendarContent,
  CEventsCalendarItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import {
  cEventsCalendarTranslations,
  type CEventsCalendarTranslations,
} from "./CEventsCalendar.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CEventsCalendar.module.css";

/* Biblioteka CMS Interakcje (komentarze + reakcje) - ładowana leniwie
   (ssr:false), montuje się i dociąga dane dopiero po wyrenderowaniu wydarzenia. */
const ArticleInteractions = dynamic(
  () =>
    import("@/components/cms/Interactions/ArticleInteractions").then(
      (m) => m.ArticleInteractions,
    ),
  { ssr: false },
);

/**
 * CEventsCalendar - visitor view komponentu `c_events_calendar` (Kalendarz wydarzeń).
 *
 * **Data source:** `C_EVENTS_CALENDAR_CONTROLLER::getApiContent()` zwraca:
 *   - `list`  - KOMPLET opublikowanych wydarzeń (`items`) + `config.per_page`.
 *     Filtr rok/miesiąc i paginacja liczone CLIENT-SIDE (kalendarzowy UX bez
 *     round-tripów do backendu).
 *   - `article` - pojedyncze wydarzenie (`text` HTML + galeria zdjęć +
 *     interakcje) - gdy URL zawiera `?article_id=N`.
 *
 * **Security:** `short_text`/`text` to HTML z panelu admina (trusted source),
 * OK dla `dangerouslySetInnerHTML`.
 */
export function CEventsCalendar({ content }: CEventsCalendarProps) {
  const t = useT(cEventsCalendarTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <ArticleView
          article={content.article}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CEventsCalendarProps = {
  content: CEventsCalendarContent;
};

/* ─── Date helpers (deterministyczne, SSR + client identyczne) ──────────── */

function yearOf(iso: string | null): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? parseInt(m[1], 10) : null;
}

function monthOf(iso: string | null): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? parseInt(m[2], 10) : null;
}

/** Format zakresu dat: "13 maja 2026" lub "13 maja 2026 - 15 maja 2026". */
function formatRange(
  start: string | null,
  end: string | null,
  t: CEventsCalendarTranslations,
): string {
  if (!start) return "";
  const startStr = t.formatDate(start);
  if (!end || end === start) return startStr;
  return `${startStr} - ${t.formatDate(end)}`;
}

/**
 * Czy wydarzenie pasuje do wybranego roku/miesiąca. Mirror logiki SQL backendu
 * (`getFilters`): wydarzenie pasuje gdy data POCZĄTKU LUB KOŃCA wpada w wybrany
 * rok (i miesiąc, gdy wybrany).
 */
function matchesFilter(
  item: CEventsCalendarItem,
  year: number | null,
  month: number | null,
): boolean {
  if (year === null) return true;

  const matchOne = (iso: string | null): boolean => {
    if (yearOf(iso) !== year) return false;
    if (month === null) return true;
    return monthOf(iso) === month;
  };

  return matchOne(item.start_date) || matchOne(item.end_date);
}

/* ─── List view ─────────────────────────────────────────────────────────── */

function ListView({
  content,
  t,
}: {
  content: CEventsCalendarContent;
  t: CEventsCalendarTranslations;
}) {
  const items: CEventsCalendarItem[] = useMemo(
    () => (Array.isArray(content.items) ? content.items : []),
    [content.items],
  );
  const perPage = Math.max(1, content.config?.per_page ?? 10);

  const [year, setYear] = useState<number | null>(null);
  const [month, setMonth] = useState<number | null>(null);

  /* Mapa rok -> dostępne miesiące (z dat początku I końca wszystkich wydarzeń). */
  const yearMonths = useMemo(() => {
    const map = new Map<number, Set<number>>();
    const add = (iso: string | null) => {
      const y = yearOf(iso);
      const mo = monthOf(iso);
      if (y === null) return;
      if (!map.has(y)) map.set(y, new Set());
      if (mo !== null) map.get(y)!.add(mo);
    };
    for (const it of items) {
      add(it.start_date);
      add(it.end_date);
    }
    return map;
  }, [items]);

  const years = useMemo(
    () => Array.from(yearMonths.keys()).sort((a, b) => b - a),
    [yearMonths],
  );

  const monthsForYear = useMemo(
    () =>
      year !== null && yearMonths.has(year)
        ? Array.from(yearMonths.get(year)!).sort((a, b) => a - b)
        : [],
    [year, yearMonths],
  );

  const filtered = useMemo(
    () => items.filter((it) => matchesFilter(it, year, month)),
    [items, year, month],
  );

  /* Tryb kliencki standardowej paginacji (`CatalogLoadMore`): "Wczytaj więcej"
     doczytuje kolejne strony slice'ując już-w-pamięci przefiltrowany zbiór.
     Zmiana filtra -> nowa referencja `filtered` -> nowy `initialItems` ->
     CatalogLoadMore resetuje się do strony 1. `updateUrl={false}` bo URL
     `/pageN` nie odzwierciedlałby przefiltrowanego zbioru. */
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const loadPage = useCallback(
    (p: number): CEventsCalendarItem[] =>
      filtered.slice((p - 1) * perPage, p * perPage),
    [filtered, perPage],
  );
  /* Stabilna referencja strony 1 - zmienia się TYLKO przy zmianie filtra
     (`filtered`), dzięki czemu CatalogLoadMore resetuje się wyłącznie wtedy,
     a nie przy każdym re-renderze. */
  const firstPage = useMemo(
    () => filtered.slice(0, perPage),
    [filtered, perPage],
  );

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  const onYearChange = (raw: string) => {
    setYear(raw === "" ? null : parseInt(raw, 10));
    setMonth(null);
  };
  const onMonthChange = (raw: string) => {
    setMonth(raw === "" ? null : parseInt(raw, 10));
  };
  const resetFilter = () => {
    setYear(null);
    setMonth(null);
  };

  return (
    <div>
      {years.length > 0 && (
        <div className={styles.filters}>
          <span className={styles.filtersLegend}>{t.filtersLegend}</span>
          <div className={styles.filterControls}>
            <select
              className={styles.filterSelect}
              value={year ?? ""}
              onChange={(e) => onYearChange(e.target.value)}
              aria-label={t.allYears}
            >
              <option value="">{t.allYears}</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            {year !== null && monthsForYear.length > 0 && (
              <select
                className={styles.filterSelect}
                value={month ?? ""}
                onChange={(e) => onMonthChange(e.target.value)}
                aria-label={t.allMonths}
              >
                <option value="">{t.allMonths}</option>
                {monthsForYear.map((mo) => (
                  <option key={mo} value={mo}>
                    {t.monthName(mo)}
                  </option>
                ))}
              </select>
            )}
            {year !== null && (
              <button
                type="button"
                className={styles.filterReset}
                onClick={resetFilter}
              >
                {t.resetFilter}
              </button>
            )}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className={styles.empty}>{t.emptyFiltered}</div>
      ) : (
        <CatalogLoadMore<CEventsCalendarItem>
          initialItems={firstPage}
          initialPage={1}
          totalPages={totalPages}
          totalCount={filtered.length}
          getKey={(item) => item.id}
          loadPage={loadPage}
          updateUrl={false}
        >
          {(allItems) => (
            <ul className={styles.list}>
              {allItems.map((item) => (
                <EventCard key={item.id} item={item} t={t} />
              ))}
            </ul>
          )}
        </CatalogLoadMore>
      )}
    </div>
  );
}

function EventCard({
  item,
  t,
}: {
  item: CEventsCalendarItem;
  t: CEventsCalendarTranslations;
}) {
  const href = extractInternalHref(item.href);
  const dateLabel = formatRange(item.start_date, item.end_date, t);

  const cardInner = (
    <>
      {item.picture && (
        <div className={styles.thumbWrap}>
          {/* Native <img> - `picture.thumb_src` to URL plików z Imperial
              backend'u (cross-origin, dynamiczne rozmiary - Imperial generuje
              miniatury). Bez Next/Image. */}
          <img
            src={item.picture.thumb_src}
            alt={item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.thumb}
            loading="lazy"
          />
        </div>
      )}
      <div className={styles.body}>
        {dateLabel && (
          <time className={styles.date} dateTime={item.start_date ?? undefined}>
            {dateLabel}
          </time>
        )}
        <h2 className={styles.title}>{item.name}</h2>
        {item.short_text && (
          <div
            className={styles.short}
            dangerouslySetInnerHTML={{ __html: item.short_text }}
          />
        )}
        <span className={styles.more}>{t.readMore}</span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </a>
      )}
    </li>
  );
}

/* ─── Article view ─────────────────────────────────────────────────────── */

function ArticleView({
  article,
  listHref,
  t,
}: {
  article: CEventsCalendarArticle;
  listHref: string | null;
  t: CEventsCalendarTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;
  const dateLabel = formatRange(article.start_date, article.end_date, t);

  return (
    <article className={styles.article}>
      {(backHref !== null || listHref) && (
        <div className={styles.articleBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.articleBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.articleBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <header className={styles.articleHeader}>
        <h2 className={styles.articleTitle}>{article.name}</h2>
        {dateLabel && (
          <time
            className={styles.articleDate}
            dateTime={article.start_date ?? undefined}
          >
            {dateLabel}
          </time>
        )}
      </header>

      {article.short_text && (
        <div
          className={styles.articleLead}
          dangerouslySetInnerHTML={{ __html: article.short_text }}
        />
      )}

      {article.text && (
        <div
          className={styles.articleBody}
          dangerouslySetInnerHTML={{ __html: article.text }}
        />
      )}

      {article.images && article.images.length > 0 && (
        <section className={styles.gallery} aria-label={t.gallery}>
          {article.images.map((img, i) => (
            <a
              key={`${img.name}-${i}`}
              href={img.full_src}
              className={styles.galleryItem}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={img.thumb_src}
                alt={img.description || article.name}
                width={img.width ?? undefined}
                height={img.height ?? undefined}
                className={styles.galleryImg}
                loading="lazy"
              />
              {img.description && (
                <span className={styles.galleryCaption}>{img.description}</span>
              )}
            </a>
          ))}
        </section>
      )}

      {/* Reakcje + komentarze (biblioteka CMS Interakcje) - lazy, pod treścią
          wydarzenia. Reakcje renderowane nad komentarzami. */}
      {article.interactions &&
        (article.interactions.reactions?.enabled ||
          article.interactions.comments?.enabled) && (
          <ArticleInteractions interactions={article.interactions} />
        )}
    </article>
  );
}
